/**
 * PeriodManager - Distribution period lifecycle: start, end, distribute, history
 */

import React, { useState } from 'react';
import { Button } from '../../ui/components/Button';
import { TextField } from '../../ui/components/TextField';
import { Badge } from '../../ui/components/Badge';
import { FeatherDownload, FeatherCheckCircle, FeatherAlertCircle, FeatherPlay } from '@subframe/core';
import {
  useActiveDistributionPeriod,
  useDistributionPeriods,
  useStartDistributionPeriod,
  useEndDistributionPeriod,
  useDistributeDistributionPeriod,
  useExportDistributionCSV,
  useRewardsConfig,
} from '../../hooks/useDistribution';
import { DistributionPreviewTable } from './DistributionPreviewTable';
import type { DistributionPeriod } from '../../api/distribution';

interface PeriodManagerProps {
  projectSlug: string;
}

export const PeriodManager: React.FC<PeriodManagerProps> = ({ projectSlug }) => {
  const { data: activeData, isLoading: activeLoading } = useActiveDistributionPeriod(projectSlug);
  const { data: periodsData, isLoading: periodsLoading } = useDistributionPeriods(projectSlug);
  const { data: configData } = useRewardsConfig(projectSlug);
  const startMutation = useStartDistributionPeriod(projectSlug);
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);

  const activePeriod = activeData && 'period' in activeData ? activeData.period : null;
  const liveStats = activeData && 'live_stats' in activeData ? (activeData as any).live_stats : null;
  const periods = periodsData?.periods || [];
  const config = configData?.config;

  if (activeLoading || periodsLoading) {
    return <div className="text-subtext-color py-4">Loading periods...</div>;
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Active / Pending Period */}
      {activePeriod ? (
        <ActivePeriodCard
          projectSlug={projectSlug}
          period={activePeriod}
          liveStats={liveStats}
        />
      ) : (
        <div className="flex flex-col gap-3 rounded border border-solid border-neutral-border p-4">
          <p className="text-body font-body text-subtext-color">
            No active distribution period. Start one to begin a new reward cycle.
          </p>
          {startMutation.error && (
            <span className="text-caption font-caption text-red-600">{startMutation.error.message}</span>
          )}
          {!config?.eligible_collection_ids?.length && (
            <span className="text-caption font-caption text-yellow-600">
              Configure eligible collections in Settings before starting a period.
            </span>
          )}
          <Button
            className="w-fit"
            variant="brand-primary"
            icon={<FeatherPlay />}
            disabled={startMutation.isPending || !config?.eligible_collection_ids?.length}
            onClick={() => startMutation.mutate({
              eligible_collection_ids: config?.eligible_collection_ids || [],
              pool_source: config?.pool_source_default,
              distribution_mode: config?.distribution_mode,
            })}
          >
            {startMutation.isPending ? 'Starting...' : 'Start Period'}
          </Button>
        </div>
      )}

      {/* Period History */}
      {periods.length > 0 && (
        <div className="flex flex-col gap-3">
          <span className="text-body-bold font-body-bold text-subtext-color">Period History</span>
          <div className="flex flex-col gap-2">
            {periods.map(period => (
              <div key={period.id}>
                <div
                  className="flex items-center justify-between rounded border border-solid border-neutral-border p-3 cursor-pointer hover:bg-default-background"
                  onClick={() => setSelectedHistoryId(selectedHistoryId === period.id ? null : period.id)}
                >
                  <div className="flex items-center gap-3">
                    <Badge variant={
                      period.status === 'active' ? 'success' :
                      period.status === 'pending_distribution' ? 'warning' : 'neutral'
                    }>
                      {period.status === 'pending_distribution' ? 'pending' : period.status}
                    </Badge>
                    <span className="text-body font-body text-default-font">
                      Period #{period.period_number}
                    </span>
                    <span className="text-caption font-caption text-subtext-color">
                      {new Date(period.started_at).toLocaleDateString()}
                      {period.ended_at ? ` - ${new Date(period.ended_at).toLocaleDateString()}` : ' - present'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-caption font-caption text-subtext-color">
                    {period.total_eligible_wallets != null && (
                      <span>{period.total_eligible_wallets} recipients</span>
                    )}
                    {period.total_pool != null && (
                      <span>Pool: {Number(period.total_pool).toLocaleString()}</span>
                    )}
                  </div>
                </div>
                {selectedHistoryId === period.id && (
                  <div className="ml-4 mt-2 mb-2">
                    <DistributionPreviewTable projectSlug={projectSlug} periodId={period.id} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ---- ActivePeriodCard ----

const ActivePeriodCard: React.FC<{
  projectSlug: string;
  period: DistributionPeriod;
  liveStats: any;
}> = ({ projectSlug, period, liveStats }) => {
  const endMutation = useEndDistributionPeriod(projectSlug);
  const distributeMutation = useDistributeDistributionPeriod(projectSlug);
  const exportMutation = useExportDistributionCSV(projectSlug);

  const [confirmEnd, setConfirmEnd] = useState(false);
  const [confirmDistribute, setConfirmDistribute] = useState(false);
  const [poolSource, setPoolSource] = useState(period.pool_source || 'compound_entries');
  const [manualPool, setManualPool] = useState('');
  const [manualRate, setManualRate] = useState('');

  const handleEnd = () => {
    const data: any = { periodId: period.id };
    if (poolSource === 'manual_pool' && manualPool) data.total_pool = parseFloat(manualPool);
    if (poolSource === 'manual_rate' && manualRate) data.manual_rate_per_point = parseFloat(manualRate);
    endMutation.mutate(data);
  };

  return (
    <div className="flex flex-col gap-4 rounded border border-solid border-neutral-border p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-body-bold font-body-bold text-default-font">
            Period #{period.period_number}
          </span>
          <Badge variant={period.status === 'active' ? 'success' : 'warning'}>
            {period.status === 'pending_distribution' ? 'pending distribution' : period.status}
          </Badge>
        </div>
        <span className="text-caption font-caption text-subtext-color">
          Started {new Date(period.started_at).toLocaleDateString()}
        </span>
      </div>

      {/* Live stats for active period */}
      {period.status === 'active' && liveStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Unassigned Compounds" value={liveStats.unassigned_compound_count} subValue={`Total: ${Number(liveStats.unassigned_compound_sum).toLocaleString()}`} />
          <StatCard label="Eligible Wallets" value={liveStats.total_eligible_wallets} />
          <StatCard label="Excluded" value={liveStats.excluded_wallet_count} />
          {liveStats.estimated_rate_per_point != null && (
            <StatCard label="Est. Rate/Point" value={Number(liveStats.estimated_rate_per_point).toFixed(6)} />
          )}
        </div>
      )}

      {/* Pending distribution stats */}
      {period.status === 'pending_distribution' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Recipients" value={period.total_eligible_wallets ?? 0} />
          <StatCard label="Total Points" value={Number(period.total_eligible_points ?? 0).toLocaleString()} />
          <StatCard label="Pool" value={Number(period.total_pool ?? 0).toLocaleString()} />
          <StatCard label="Rate/Point" value={period.rate_per_point != null ? Number(period.rate_per_point).toFixed(6) : '-'} />
        </div>
      )}

      {/* Snapshot info */}
      {period.status === 'pending_distribution' && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-caption font-caption text-subtext-color">
          {period.coreum_usd_price && <span>COREUM: ${Number(period.coreum_usd_price).toFixed(4)}</span>}
          {period.xrp_usd_price && <span>XRP: ${Number(period.xrp_usd_price).toFixed(4)}</span>}
          {period.royalty_total_drops != null && (
            <span>Royalties: {(Number(period.royalty_total_drops) / 1_000_000).toFixed(2)} XRP ({period.royalty_sale_count} sales)</span>
          )}
          {period.total_excluded_wallets != null && <span>Excluded: {period.total_excluded_wallets}</span>}
          {period.total_unregistered_wallets != null && <span>Unregistered: {period.total_unregistered_wallets}</span>}
        </div>
      )}

      {/* Actions for active period */}
      {period.status === 'active' && (
        <div className="flex flex-col gap-3">
          {/* Pool source selection */}
          <div className="flex flex-col gap-2 p-3 rounded border border-solid border-neutral-border">
            <span className="text-caption-bold font-caption-bold text-default-font">End Period - Pool Source</span>
            <div className="flex gap-4">
              {['compound_entries', 'manual_pool', 'manual_rate'].map(src => (
                <label key={src} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={poolSource === src} onChange={() => setPoolSource(src)} />
                  <span className="text-body font-body text-default-font">
                    {src === 'compound_entries' ? 'Compounds' : src === 'manual_pool' ? 'Manual Pool' : 'Manual Rate'}
                  </span>
                </label>
              ))}
            </div>
            {poolSource === 'manual_pool' && (
              <TextField className="h-auto w-64" label="Pool Amount">
                <TextField.Input type="number" step="any" min="0" value={manualPool} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setManualPool(e.target.value)} />
              </TextField>
            )}
            {poolSource === 'manual_rate' && (
              <TextField className="h-auto w-64" label="Rate Per Point">
                <TextField.Input type="number" step="any" min="0" value={manualRate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setManualRate(e.target.value)} />
              </TextField>
            )}
          </div>

          {confirmEnd ? (
            <div className="flex items-center gap-3 p-3 rounded border border-solid border-yellow-600/30 bg-yellow-600/10">
              <FeatherAlertCircle className="h-4 w-4 text-yellow-500" />
              <span className="text-caption font-caption text-default-font">
                This will calculate rewards for all eligible holders and assign compounds to this period.
              </span>
              <Button variant="destructive-primary" size="small" disabled={endMutation.isPending} onClick={handleEnd}>
                {endMutation.isPending ? 'Calculating...' : 'Confirm End'}
              </Button>
              <Button variant="neutral-secondary" size="small" onClick={() => setConfirmEnd(false)}>Cancel</Button>
            </div>
          ) : (
            <div className="flex justify-end">
              <Button variant="neutral-secondary" onClick={() => setConfirmEnd(true)}>
                End Period & Calculate
              </Button>
            </div>
          )}

          {endMutation.error && (
            <span className="text-caption font-caption text-red-600">{endMutation.error.message}</span>
          )}
        </div>
      )}

      {/* Actions for pending_distribution */}
      {period.status === 'pending_distribution' && (
        <div className="flex flex-col gap-3">
          <DistributionPreviewTable projectSlug={projectSlug} periodId={period.id} showExcluded />

          <div className="flex items-center justify-between">
            <Button
              variant="neutral-secondary"
              icon={<FeatherDownload />}
              disabled={exportMutation.isPending}
              onClick={() => exportMutation.mutate(period.id)}
            >
              {exportMutation.isPending ? 'Downloading...' : 'Export CSV'}
            </Button>

            {confirmDistribute ? (
              <div className="flex items-center gap-2">
                <span className="text-caption font-caption text-subtext-color">Mark as distributed?</span>
                <Button variant="brand-primary" size="small" disabled={distributeMutation.isPending} onClick={() => distributeMutation.mutate(period.id)}>
                  {distributeMutation.isPending ? 'Distributing...' : 'Confirm'}
                </Button>
                <Button variant="neutral-secondary" size="small" onClick={() => setConfirmDistribute(false)}>Cancel</Button>
              </div>
            ) : (
              <Button variant="brand-primary" icon={<FeatherCheckCircle />} onClick={() => setConfirmDistribute(true)}>
                Mark as Distributed
              </Button>
            )}
          </div>

          {distributeMutation.error && (
            <span className="text-caption font-caption text-red-600">{distributeMutation.error.message}</span>
          )}
        </div>
      )}

      {/* Distributed period */}
      {period.status === 'distributed' && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FeatherCheckCircle className="h-4 w-4 text-green-600" />
            <span className="text-body font-body text-green-700">
              Distributed on {period.distributed_at ? new Date(period.distributed_at).toLocaleDateString() : 'N/A'}
            </span>
          </div>
          <Button
            variant="neutral-secondary"
            size="small"
            icon={<FeatherDownload />}
            disabled={exportMutation.isPending}
            onClick={() => exportMutation.mutate(period.id)}
          >
            Export CSV
          </Button>
        </div>
      )}
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: string | number; subValue?: string }> = ({
  label,
  value,
  subValue,
}) => (
  <div className="rounded border border-solid border-neutral-border p-3">
    <span className="text-caption font-caption text-subtext-color block">{label}</span>
    <span className="text-body-bold font-body-bold text-default-font">{value}</span>
    {subValue && <span className="text-caption font-caption text-subtext-color block">{subValue}</span>}
  </div>
);
